import { apiFetch } from "@/integrations/backend/client";
import type { Difficulty } from "./enums";
import { getMyProfile } from "./profiles";

export type Program = {
  id: string;
  name: string;
  description: string;
  duration: number;
  frequency: number;
  weekly_schedule_preference: string;
  difficulty: Difficulty;
  is_template: boolean;
  motivations: string[];
  exercise_types: string[];
  exercise_type_details: Record<string, string>;
  program_focus: string;
  what_to_expect: string;
  variety: string;
  created_at: string | null;
  updated_at: string | null;
};

export type ClientProgram = {
  id: string;
  profile_id: string;
  program_id: string;
  program: Program;
  assigned_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CreateProgramInput = {
  name: string;
  description: string;
  duration: number;
  frequency: number;
  weekly_schedule_preference?: string;
  difficulty: Difficulty;
  motivations: string[];
  exercise_types: string[];
  exercise_type_details?: Record<string, string>;
  program_focus?: string;
  what_to_expect?: string;
  variety?: string;
};

export type UpdateProgramInput = Partial<CreateProgramInput>;

export async function listPrograms(): Promise<Program[]> {
  const res = await apiFetch("/api/v1/programs", { method: "GET" });
  if (!res.ok) throw new Error(`programs_list_failed:${res.status}`);
  const data = (await res.json()) as Program[];
  return data ?? [];
}

export async function getProgram(id: string): Promise<Program> {
  const res = await apiFetch(`/api/v1/programs/${id}`, { method: "GET" });
  if (!res.ok) throw new Error(`program_get_failed:${res.status}`);
  return (await res.json()) as Program;
}

export async function createProgram(input: CreateProgramInput): Promise<Program> {
  const res = await apiFetch("/api/v1/programs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `program_create_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as Program;
}

export async function updateProgram(id: string, updates: UpdateProgramInput): Promise<Program> {
  const res = await apiFetch(`/api/v1/programs/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `program_update_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as Program;
}

export async function deleteProgram(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/programs/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `program_delete_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
}

export async function duplicateProgram(id: string, name?: string): Promise<Program> {
  const res = await apiFetch(`/api/v1/programs/${id}/duplicate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `program_duplicate_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as Program;
}

export async function getClientPrograms(profileId: string): Promise<ClientProgram[]> {
  const res = await apiFetch(`/api/v1/client-programs?profile_id=${profileId}`, { method: "GET" });
  if (!res.ok) throw new Error(`client_programs_get_failed:${res.status}`);
  const data = (await res.json()) as ClientProgram[];
  return data ?? [];
}

export async function getMyCurrentProgram(): Promise<ClientProgram | null> {
  const me = await getMyProfile();
  const rows = await getClientPrograms(me.id);
  // Backend orders by "-assigned_at", so the first row is the latest assignment.
  return rows[0] ?? null;
}

export async function assignProgramToClient(profileId: string, programId: string): Promise<ClientProgram> {
  const res = await apiFetch("/api/v1/client-programs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile_id: profileId, program_id: programId }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_assign_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as ClientProgram;
}

export async function assignProgramTemplateToClient(profileId: string, programId: string): Promise<ClientProgram> {
  const res = await apiFetch(`/api/v1/programs/${programId}/assign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile_id: profileId }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_template_assign_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as ClientProgram;
}

export async function createProgramForClient(profileId: string, input: CreateProgramInput): Promise<ClientProgram> {
  const res = await apiFetch("/api/v1/client-programs/create-scratch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile_id: profileId, ...input }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_create_scratch_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as ClientProgram;
}

export async function updateAssignedProgram(
  clientProgramId: string,
  updates: UpdateProgramInput
): Promise<ClientProgram> {
  const res = await apiFetch(`/api/v1/client-programs/${clientProgramId}/program`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_assigned_program_update_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as ClientProgram;
}

export async function updateClientProgram(clientProgramId: string, programId: string): Promise<ClientProgram> {
  const res = await apiFetch(`/api/v1/client-programs/${clientProgramId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ program_id: programId }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_update_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as ClientProgram;
}

export async function unassignProgram(clientProgramId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/client-programs/${clientProgramId}`, { method: "DELETE" });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `client_program_unassign_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
}

